import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@carepass/api-client";
import { memberApi } from "../shared/api";
import type { Transaction } from "../shared/types";
import { invalidateTransactionRelated } from "./transaction-cache";

const approvedStates = new Set(["TRANSFER_SELECTION_PENDING", "TRANSFER_READY", "TRANSFER_PROCESSING", "TRANSFER_UNKNOWN", "TRANSFER_FAILED", "COMPLETED"]);
const pollingStates = new Set(["PAYMENT_PROCESSING", "PAYMENT_UNKNOWN", "TRANSFER_READY", "TRANSFER_PROCESSING", "TRANSFER_UNKNOWN"]);

export function isTransactionPaid(transaction: Transaction): boolean {
  return approvedStates.has(transaction.status) || (transaction.paymentStatus === "APPROVED"
    && !["PAYMENT_PENDING", "PAYMENT_FAILED", "CANCELED", "PAYMENT_EXPIRED"].includes(transaction.status));
}

export function settlementError(cause: unknown, fallback: string): string {
  return cause instanceof ApiError || cause instanceof Error ? cause.message : fallback;
}

export function useSettlementTransaction(transactionId: number) {
  const client = useQueryClient();
  const [pollUntil, setPollUntil] = useState(() => Date.now() + 60_000);
  const query = useQuery({
    queryKey: ["member", "transaction", transactionId],
    queryFn: () => memberApi.transaction(transactionId),
    refetchInterval: value => {
      const transaction = value.state.data;
      // Scheduled READY remains a normal reservation, not a continuously loading page.
      return transaction && pollingStates.has(transaction.status)
        && !(transaction.status === "TRANSFER_READY" && transaction.transferScheduleType === "SCHEDULED")
        && Date.now() < pollUntil ? 2500 : false;
    },
  });
  const previous = useRef<{ id: number; status?: string }>({ id: transactionId, status: query.data?.status });
  useEffect(() => { setPollUntil(Date.now() + 60_000); }, [transactionId, query.data?.status]);
  useEffect(() => {
    const status = query.data?.status;
    if (previous.current.id === transactionId && previous.current.status && status && previous.current.status !== status) {
      invalidateTransactionRelated(client);
    }
    previous.current = { id: transactionId, status };
  }, [client, transactionId, query.data?.status]);
  const refresh = async () => {
    setPollUntil(Date.now() + 60_000);
    const result = await query.refetch();
    return result.isError ? undefined : result.data;
  };
  return { query, refresh };
}
